import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSnapshot } from "../../hooks/useSnapshot";
import { planPagos } from "../../utils/planPagos";
import dayjs from "dayjs";
import Form from "../Form";
import FormCompany from "../FormCompany";
import FormCodeudor from "../FormCodeudor";
import TablePagos from "../../pages/plataforma/plan-de-pagos/table";
import LogoWhite from "../LogoWhite";
import LogoBlack from "../LogoBlack";

function Banner({ company, codeudor, dataCodeudor }) {
  const { data: tasa, isFetching } = useSnapshot("tasa");
  const [isLoaded, setIsLoaded] = useState(false);
  const [sended, setSended] = useState(false);
  const [step, setStep] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [data, setData] = useState({
    knowMoney: true,
    ammount: "5000000",
    month: "12",
    expedition: "",
    name: "",
    lastName: "",
    company: "",
    NIT: "",
    address: "",
    cellphone: "",
    identity: "",
    email: "",
  });
  const [files, setFiles] = useState({
    cert_laboral: "",
    comp_nomina: "",
    fot_cedula: "",
    dec_renta: "",
  });

  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const { schedule, totalCredit, totalInterest } = planPagos({
    ...data,
    tasa: tasa[0]?.number,
    start_date: dayjs().format("YYYY-MM-DD"),
  });

  const getStepTitle = () => {
    if (company) return "Solicitud Empresarial";
    if (codeudor) return "Información del Codeudor";
    return "Solicitud de Crédito Personal";
  };

  const getStepDescription = () => {
    if (company)
      return "Complete la información de su empresa para procesar la solicitud";
    if (codeudor) return "Proporcione los datos del codeudor para continuar";
    return "Complete sus datos personales para solicitar su crédito";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getThemeColors = () => {
    if (hostname === "creditos.sofia.com.co") {
      return {
        primary: "bg-gray-900",
        hover: "hover:bg-gray-800",
        text: "text-gray-900",
        accent: "bg-gray-100",
      };
    }
    return {
      primary: "bg-[#49b0a8]",
      hover: "hover:bg-[#3a8a84]",
      text: "text-[#49b0a8]",
      accent: "bg-[#49b0a8]/10",
    };
  };

  const theme = getThemeColors();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header
        className={`${theme.primary} shadow-lg transition-all duration-300 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            {/* Logo */}
            <div className="flex-shrink-0 -mt-6">
              {hostname === "creditos.sofia.com.co" ? (
                <img
                  src="https://sofia.com.co/logo.webp"
                  width={200}
                  className="h-12 w-auto mt-[20px]"
                  alt="Sofia Logo"
                />
              ) : (
                <LogoWhite size={150} />
              )}
            </div>

            {/* Admin Portal Button */}
            <div className="hidden md:block">
              <Link to="/plataforma">
                <button
                  className={`
                    group relative overflow-hidden bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-xl
                    font-medium transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-lg
                    border border-white/20 hover:bg-white/20 flex items-center gap-2
                  `}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    className="icon"
                    stroke="#ffffff"
                    fill="#ffffff"
                    viewBox="0 0 1024 1024"
                  >
                    <path d="m981.534 248.354-.403-2.016a29 29 0 0 0-1.747-5.511c-.135-.404-.27-.941-.538-1.345-.941-2.15-2.016-4.033-3.226-5.915l-1.076-1.478c-1.075-1.479-2.15-2.823-3.495-4.168-.403-.403-.806-.94-1.21-1.344a41.6 41.6 0 0 0-5.242-4.436c-.27-.134-.672-.403-.941-.538-1.21-.806-2.42-1.613-3.764-2.285L529.724 4.234c-11.426-5.645-24.734-5.645-36.026 0L63.53 219.318c-1.344.672-2.554 1.479-3.764 2.285-.268.135-.672.404-.94.538a41.6 41.6 0 0 0-5.243 4.436c-.403.404-.807.807-1.21 1.344-1.21 1.345-2.42 2.689-3.495 4.168l-1.076 1.478c-1.21 1.882-2.285 3.899-3.226 5.915-.134.404-.269.941-.538 1.345-.672 1.747-1.21 3.63-1.747 5.511l-.403 2.016c-.404 2.42-.673 4.84-.673 7.26v512.974c0 15.325 8.604 29.17 22.315 36.027l430.168 215.083c.269.135.672.27 1.075.404 1.882.94 4.033 1.613 6.05 2.15.806.27 1.478.538 2.285.673 2.823.672 5.78 1.075 8.737 1.075s5.915-.403 8.738-1.075c.807-.135 1.479-.538 2.286-.673 2.15-.537 4.167-1.344 6.049-2.15.269-.135.672-.27 1.075-.404l430.168-215.083a40.33 40.33 0 0 0 22.314-36.027V255.614c-.268-2.42-.537-4.84-.94-7.26M511.711 85.563l339.967 170.05-123.136 61.568-199.087-97.728c-.268-.135-.672-.135-.94-.27-1.748-.806-3.63-1.478-5.512-2.016a59 59 0 0 0-2.554-.806 46 46 0 0 0-6.184-.807c-.806 0-1.613-.269-2.42-.269h-.268c-5.78 0-11.292 1.21-16.266 3.495-.134 0-.269.135-.403.135l-207.556 94.502-115.608-57.803zm0 596.454L372.58 612.518l138.729-77.968 136.443 79.447zm176.772-294.665v157.146l-136.444-79.581v-144.51zm-217.1 77.162-144.51 81.194V384.126l144.51-65.87zM121.872 512.1V320.81l124.345 62.24v231.483c0 .673.135 1.345.269 2.151l.403 4.302.807 3.898c.269 1.21.672 2.42 1.075 3.63.538 1.344 1.076 2.688 1.748 4.033.269.672.538 1.21.806 1.882.135.403.538.537.673.94 1.613 2.824 3.495 5.378 5.78 7.663l.403.403a42 42 0 0 0 9.679 6.99c.538.27.94.538 1.479.807.403.135.672.403.94.672l201.104 100.149V918.34L121.872 743.585zM901.55 743.585 552.04 918.34V752.054l194.784-97.326a40.29 40.29 0 0 0 22.18-38.85c0-.403.135-.806.135-1.21V387.084l132.411-66.272z"></path>
                  </svg>
                  <span>Portal Administrador</span>
                </button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <Link to="/plataforma">
                <button className="text-white p-2 rounded-lg hover:bg-white/10 transition-colors duration-200">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 12h18M3 6h18M3 18h18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {step === 2 || sended ? (
          /* Credit Preview Section */
          <div
            className={`transition-all duration-700 ease-out ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-10 h-10 bg-green-100 rounded-full mb-4">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="text-green-600"
                  >
                    <path
                      d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {step === 2 ? "Revisa tu Crédito" : "¡Solicitud Enviada!"}
                </h1>
                <p className="text-gray-600 text-lg">
                  {step === 2
                    ? "Revisa los detalles de tu crédito antes de continuar"
                    : "Tu solicitud ha sido enviada correctamente. Este sería tu plan de pagos:"}
                </p>
              </div>

              {/* Credit Summary */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <div className="text-sm text-gray-600 mb-1">
                      Monto Solicitado
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatCurrency(Number(data?.ammount))}
                    </div>
                  </div>
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <div className="text-sm text-gray-600 mb-1">Tasa Anual</div>
                    <div className="text-2xl font-bold text-[#49b0a8]">
                      {Number(tasa[0]?.number).toFixed(2)}%
                    </div>
                  </div>
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <div className="text-sm text-gray-600 mb-1">Plazo</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {data?.month} meses
                    </div>
                  </div>
                </div>

                {/* Action Buttons for Step 2 */}
                {step === 2 && (
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-0">
                    <button
                      onClick={() => setStep(0)}
                      className="px-8 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors duration-200"
                    >
                      Modificar Datos
                    </button>
                    <button
                      onClick={() => setStep(1)}
                      className={`
                        group relative overflow-hidden ${theme.primary} ${theme.hover} text-white px-8 py-3 rounded-xl
                        font-medium transition-all duration-300 ease-out transform hover:scale-105 hover:shadow-lg
                        flex items-center justify-center gap-2
                      `}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="relative z-10"
                      >
                        <path
                          d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className="relative z-10">
                        Continuar con la Solicitud
                      </span>
                    </button>
                  </div>
                )}
              </div>

              {/* Payment Schedule */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-xl font-semibold text-gray-900  justify-center flex items-center gap-2">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-[#49b0a8]"
                    >
                      <path
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Plan de Pagos Detallado
                  </h2>
                  <p className="text-gray-600 mt-1">
                    Cronograma completo de amortización de tu crédito
                  </p>
                </div>
                <div className="p-8">
                  <TablePagos
                    landing
                    loading={isFetching}
                    schedule={schedule}
                  />
                </div>
              </div>

              {/* Additional Info */}
              {sended && (
                <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="text-blue-600"
                      >
                        <path
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-blue-900 font-medium mb-1">
                        ¿Qué sigue?
                      </h3>
                      <ul className="text-blue-800 text-sm space-y-1">
                        <li>
                          • Revisaremos tu solicitud en las próximas 24 horas
                        </li>
                        <li>• Te contactaremos para confirmar los detalles</li>
                        <li>
                          • Una vez aprobado, procederemos con el desembolso
                        </li>
                        <li>• Recibirás todas las actualizaciones por email</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Form Section */
          <div
            className={`transition-all duration-700 ease-out delay-200 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              {/* Form Header */}
              <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                  {getStepTitle()}
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  {getStepDescription()}
                </p>

                {/* Progress Indicator */}
                {!company && !codeudor && (
                  <div className="max-w-md mx-auto">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors duration-200 ${
                            step >= 0
                              ? "bg-[#49b0a8] text-white"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          1
                        </div>
                        <span className="ml-2 text-sm text-gray-600">
                          Datos Personales
                        </span>
                      </div>
                      <div className="flex-1 mx-4">
                        <div className="h-1 bg-gray-200 rounded-full">
                          <div
                            className={`h-1 rounded-full transition-all duration-500 ${
                              step >= 1
                                ? "bg-[#49b0a8] w-full"
                                : "bg-gray-200 w-0"
                            }`}
                          ></div>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors duration-200 ${
                            step >= 1
                              ? "bg-[#49b0a8] text-white"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          2
                        </div>
                        <span className="ml-2 text-sm text-gray-600">
                          Documentos
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Form Container */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-8">
                  {company ? (
                    <FormCompany />
                  ) : codeudor ? (
                    <FormCodeudor dataCodeudor={dataCodeudor} />
                  ) : (
                    <Form
                      setSended={setSended}
                      sended={sended}
                      data={data}
                      setData={setData}
                      setFiles={setFiles}
                      files={files}
                      step={step}
                      setStep={setStep}
                    />
                  )}
                </div>
              </div>

              {/* Quick Preview Button */}
              {!company && !codeudor && data?.ammount && data?.month && (
                <div className="mt-8 text-center">
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className="inline-flex items-center gap-2 text-[#49b0a8] hover:text-[#3a8a84] font-medium transition-colors duration-200"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {showPreview ? "Ocultar" : "Ver"} Vista Previa del Crédito
                  </button>

                  {/* Quick Preview */}
                  {showPreview && (
                    <div className="mt-6 bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Monto
                          </div>
                          <div className="font-semibold text-gray-900">
                            {formatCurrency(Number(data?.ammount))}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Plazo
                          </div>
                          <div className="font-semibold text-gray-900">
                            {data?.month} meses
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Tasa</div>
                          <div className="font-semibold text-[#49b0a8]">
                            {tasa[0]?.number}% anual
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Cuota Mensual
                          </div>
                          <div className="font-semibold text-gray-900">
                            {schedule[0]
                              ? formatCurrency(schedule[0].cuotaAPagar)
                              : "Calculando..."}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-4 flex items-center justify-center">
              {hostname === "creditos.sofia.com.co" ? (
                <img
                  src="https://sofia.com.co/logo.webp"
                  width={200}
                  className="h-12 w-auto mt-[20px]"
                  alt="Sofia Logo"
                />
              ) : (
                <LogoWhite size={120} />
              )}
            </div>
            <p className="text-gray-400 text-sm">
              © 2025 Sistema de Créditos. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Banner;
